import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { CSSProperties } from 'react';
import {
  palette,
  gradient,
  spacing,
  radius,
  shadow,
  typography,
  transition,
  duration,
  easing,
  layout,
  status,
} from '../../style-utils/theme';
import { fadeInUp, fadeInDown, scaleIn } from '../../style-utils/motion';
import { down, shortLandscape } from '../../style-utils/media-queries';

/* Local, login-only ambient keyframes (brand hero sheen + glow drift). These
 * are intentionally subtle and honour prefers-reduced-motion via index.css. */
const heroDrift = keyframes`
  0% { transform: translate3d(-6%, -4%, 0) scale(1); opacity: 0.55; }
  50% { transform: translate3d(6%, 5%, 0) scale(1.18); opacity: 0.85; }
  100% { transform: translate3d(-6%, -4%, 0) scale(1); opacity: 0.55; }
`;

const heroSheen = keyframes`
  0% { transform: translateX(-130%) skewX(-14deg); }
  100% { transform: translateX(240%) skewX(-14deg); }
`;

/* Legacy inline-style eye icon — retained for backwards compatibility (other
 * partitions may reference it). The login form itself now uses PasswordToggle. */
export const eyeIconStyles: CSSProperties = {
  position: 'absolute',
  zIndex: '1',
  bottom: '10px',
  cursor: 'pointer',
  width: '20px',
  height: '20px',
  right: '15px',
  color: palette.slate,
};

export const LogInWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  padding: ${spacing.xl};
  background: ${palette.porcelain};
  overflow: hidden;
  isolation: isolate;

  /* Soft ambient iris wash behind the card — depth without noise. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background: radial-gradient(
        58% 55% at 16% 10%,
        ${palette.tealWash} 0%,
        ${palette.tealTransparent} 60%
      ),
      radial-gradient(
        52% 50% at 90% 94%,
        ${palette.tealWashSoft} 0%,
        ${palette.tealTransparent} 62%
      );
  }

  ${down('md')} {
    padding: 0;
    align-items: stretch;
  }

  /* A phone in landscape is WIDE — 932px on a Pro Max — so it never reaches the
     down('md') rule above and takes the full two-column desk layout in 430px of
     height. The two columns are right there; only the breathing room is wrong. */
  ${shortLandscape} {
    padding: ${spacing.sm};
    align-items: flex-start;
  }
`;

export const LogInFormContainer = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  width: 100%;
  max-width: 968px;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.lg};
  overflow: hidden;
  animation: ${scaleIn} ${duration.slower} ${easing.entrance} both;

  ${down('md')} {
    flex-direction: column-reverse;
    max-width: 480px;
    border-radius: ${radius.md};
  }

  ${down('sm')} {
    max-width: none;
    min-height: 100vh;
    min-height: 100dvh;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
`;

export const LogInForm = styled.form`
  position: relative;
  flex: 1 1 56%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${spacing.xxl} ${spacing.xl};

  ${down('md')} {
    flex: 1 1 auto;
    padding: ${spacing.xl} ${spacing.sm} ${spacing.xxl};
  }

  ${shortLandscape} {
    padding: ${spacing.md} ${spacing.lg};
  }
`;

/* AN <h1>, NOT A <p>. The product's front door had no heading element at
   all — the only landmark on the page was the form itself, so a screen
   reader arriving here was told nothing about what the page is. */
export const LogInTitle = styled.h1`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
  animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;
  animation-delay: 120ms;
`;

export const LogInSubTitle = styled.p`
  margin: ${spacing.xs} 0 ${spacing.lg};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.regular};
  line-height: ${typography.leading.normal};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.slate};
  animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;
  animation-delay: 170ms;
`;

export const TextContainer = styled.div`
  position: relative;
  flex: 1 1 44%;
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  padding: ${spacing.xxl} ${spacing.xl};
  overflow: hidden;
  isolation: isolate;
  color: ${palette.white};
  background: ${gradient.brand};

  /* Slow-drifting iris-glow highlight. */
  &::before {
    content: '';
    position: absolute;
    z-index: 0;
    top: -28%;
    left: -22%;
    width: 78%;
    height: 78%;
    pointer-events: none;
    background: radial-gradient(
      circle at 32% 32%,
      ${palette.tealWash} 0%,
      ${palette.tealTransparent} 60%
    );
    filter: blur(6px);
    animation: ${heroDrift} 17s ${easing.standard} infinite;
  }

  /* Faint diagonal sheen sweep. */
  &::after {
    content: '';
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    width: 55%;
    height: 100%;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      ${palette.tealTransparent} 0%,
      ${palette.onBrandSubtle} 50%,
      ${palette.tealTransparent} 100%
    );
    transform: translateX(-130%) skewX(-14deg);
    animation: ${heroSheen} 12s ${easing.standard} infinite;
    animation-delay: 3.5s;
  }

  ${down('md')} {
    flex: none;
    gap: ${spacing.sm};
    padding: ${spacing.xl} ${spacing.sm};
  }

  ${shortLandscape} {
    gap: ${spacing.sm};
    padding: ${spacing.md} ${spacing.lg};
  }
`;

export const BrandRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  row-gap: ${spacing.xs};
  align-items: center;
  gap: ${spacing.sm};
  animation: ${fadeInDown} ${duration.slow} ${easing.entrance} both;
  animation-delay: 120ms;
`;

/* The legal links, on their own line directly under the logo. Beside it they
   had room for three of six and wrapped into a block. */
export const LegalCorner = styled.div`
  flex: 1 0 100%;
  display: flex;
  justify-content: flex-start;
`;

export const CompanyTitle = styled.h4`
  margin: 0;
  font-family: ${typography.display};
  font-size: ${typography.size.xxl}px;
  font-weight: ${typography.weight.heavy};
  letter-spacing: ${typography.tracking.tight};
  line-height: ${typography.leading.tight};
  color: ${palette.white};
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  animation: ${fadeInUp} ${duration.slow} ${easing.entrance} both;
  animation-delay: 220ms;
`;

export const HeroEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  text-transform: uppercase;
  letter-spacing: ${typography.tracking.caps};
  color: ${palette.onBrandMuted};
`;

export const Description = styled.p`
  margin: 0;
  max-width: 32ch;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.snug};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.onBrandStrong};
`;

export const HeroMeta = styled.p`
  margin: 0;
  max-width: 42ch;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.regular};
  line-height: ${typography.leading.normal};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.onBrandMuted};
`;

export const ButtonContainer = styled.button<{ isLoading?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  width: 100%;
  height: ${layout.buttonHeight};
  margin-top: ${spacing.lg};
  padding: 0 ${spacing.md};
  border: none;
  border-radius: ${radius.sm};
  background: ${gradient.primary};
  box-shadow: ${shadow.brand};
  color: ${palette.white};
  cursor: ${({ isLoading }) => (isLoading ? 'progress' : 'pointer')};
  overflow: hidden;
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};
  animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;
  animation-delay: 240ms;

  & svg {
    transition: transform ${duration.fast} ${easing.standard};
  }

  &:hover {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }

  &:hover svg {
    transform: translateX(3px);
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus}, ${shadow.brand};
  }

  &:disabled {
    background: ${palette.lineStrong};
    color: ${palette.slateLight};
    box-shadow: none;
    cursor: default;
    transform: none;
  }

  /* Finished: the button keeps the brand fill it had when it was pressed,
     so the tick lands on the same surface and not on the disabled grey. */
  &[data-login-state='done']:disabled {
    background: ${gradient.primary};
    color: ${palette.white};
    box-shadow: ${shadow.brand};
  }
`;

/* The finished state, laid over the (hidden) label in the same box. */
export const ButtonSuccess = styled.span`
  position: absolute;
  inset: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  color: ${palette.white};

  & svg {
    width: 20px;
    height: 20px;
  }
`;

export const ButtonContent = styled.span<{ $hidden?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
`;

export const ButtonTitle = styled.span`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: inherit;
`;

/* Constrains the shared 65px Spinner down to an in-button pending indicator. */
export const ButtonSpinner = styled.span`
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  & svg {
    width: 24px;
    height: 24px;
    margin: 0;
    transform: none !important;
  }

  & circle {
    stroke: ${palette.white} !important;
  }
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  &:nth-of-type(2) {
    margin-top: ${spacing.sm};
    position: relative;
  }
`;

export const Label = styled.label`
  margin-bottom: ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.inkSoft};
`;

export const StyledInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid
    ${({ hasError }) => (hasError ? palette.alert : palette.line)};
  background: ${palette.surface};
  box-shadow: ${({ hasError }) => (hasError ? shadow.focusError : shadow.xs)};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors};

  &::placeholder {
    color: ${palette.slateLight};
  }

  &:hover {
    border-color: ${({ hasError }) =>
      hasError ? palette.alert : palette.lineStrong};
  }

  &:focus {
    outline: none;
    border-color: ${({ hasError }) =>
      hasError ? palette.alert : palette.iris};
    box-shadow: ${({ hasError }) =>
      hasError ? shadow.focusError : shadow.focus};
  }
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: ${spacing.half};
  bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  padding: 0;
  border: none;
  background: transparent;
  color: ${palette.slate};
  cursor: pointer;
  border-radius: ${radius.xs};
  transition: ${transition.colors};

  &:hover {
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const PasswordEye = styled.i<{ visible?: boolean }>`
  position: absolute;
  top: 50%;
  left: 50%;
  font-size: ${typography.size.md}px;
  line-height: 1;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: translate(-50%, -50%)
    ${({ visible }) => (visible ? 'scale(1)' : 'scale(0.7)')};
  transition: opacity ${duration.fast} ${easing.standard},
    transform ${duration.fast} ${easing.standard};
  pointer-events: none;
`;

export const ErrorMessage = styled.p`
  display: flex;
  align-items: center;
  margin: ${spacing.xs} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.normal};
  letter-spacing: ${typography.tracking.snug};
  color: ${status.alert.fg};
`;
